/* eslint-disable no-nested-ternary */
// @ts-check
import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { classNames, setTrackDataLayer } from '../../utils';
import { OPEN_CART_EVENT } from '../../components/cart-modal';

const PRIVATE_PRICE_ATTRIBUTES = [
  'PrivatePriceTitle',
  'PrivatePriceDescription',
  'PrivatePrice'
];

function ProductCard({
  addToCardLabel,
  cartEndpoint,
  loginToPurchaseLabel,
  unableToAddToCart,
  pricesLabels,
  listNameSufix,
  Title,
  Link,
  Author,
  Thumb,
  Prices,
  Description,
  ProductKey,
  SitecoreId,
  IsAuthenticated,
  IsMember,
  LoginUrl,
  PublishedDateText,
  ProductOffSiteUrl,
  ProductImageFlags
}) {
  const id = useId();

  function handleSubmit(e) {
    e.preventDefault();
    fetch(cartEndpoint, {
      method: 'POST',
      body: JSON.stringify({
        ProductKey,
        Quantity: '1',
        SitecoreId
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((res) => res.json())
      .then((res) => {
        const product = res.data;
        const productInfo = {
          item_name: product?.ItemName,
          item_id: product?.ItemId,
          price: Number(product?.Price),
          item_category: product?.Category,
          item_category2: product?.Category2,
          item_category3: product?.Category3,
          item_category4: product?.Category4,
          item_category5: product?.Category5,
          item_brand: product?.Brand,
          list_id: 'product-listing',
          list_name: `product-listing-${listNameSufix}`,
          quantity: Number(product?.Quantity)
        };
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.dispatchEvent(new Event(OPEN_CART_EVENT));
        setTrackDataLayer({
          eventName: 'add_to_cart',
          custom: {
            ecommerce: {
              items: [productInfo]
            }
          }
        });
      })
      .catch(console.error);
  }

  return (
    <article className="product-card" aria-labelledby={`product-${id}`}>
      {Thumb?.Src ? (
        <div
          className={`picture product-card__image ${
            Thumb.ImageOverlay ? 'picture--has-badge' : ''
          }`}
        >
          {Thumb.ImageOverlay ? (
            <div
              className={`picture__badge picture__badge--${Thumb.ImageOverlayLocation}`}
            >
              <img src={Thumb.ImageOverlay} alt={Thumb.Alt} />
            </div>
          ) : null}
          <picture>
            <img
              width={500}
              height={280}
              src={Thumb.Src}
              alt={Thumb?.Alt}
              loading="lazy"
            />
          </picture>
          {ProductImageFlags && ProductImageFlags.length > 0 && (
            <div className="product-card__flag">
              {ProductImageFlags.map((flag, index) => (
                <span
                  key={index}
                  className={classNames(
                    'product-card__flag-item',
                    flag?.FlagDark === true && 'product-card__flag-item--dark'
                  )}
                  style={{
                    '--flag-color': flag?.FlagColor,
                    backgroundColor: flag?.FlagColor
                  }}
                >
                  {flag?.FlagText}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
      <div className="product-card__content">
        <h3 className="product-card__title" id={`product-${id}`}>
          <a href={Link} dangerouslySetInnerHTML={{ __html: Title }} />
        </h3>
        {Author ? <p className="product-card__author">{Author}</p> : null}
        {Description ? (
          <p
            className="product-card__description"
            dangerouslySetInnerHTML={{ __html: Description }}
          />
        ) : null}
        {PublishedDateText ? (
          <p className="product-card__release-date">{PublishedDateText}</p>
        ) : null}
        <div className="product-card__prices primary">
          {Object.entries(Prices)
            .filter(([, value]) => !!value)
            .map(([key, value]) =>
              !PRIVATE_PRICE_ATTRIBUTES.includes(key) ? (
                <span
                  className={classNames(key === 'MemberPrice' && 'color-apple')}
                  key={key}
                >
                  <strong>
                    {pricesLabels[key] || ''} ${value}{' '}
                  </strong>
                </span>
              ) : null
            )}
          {Prices.PrivatePrice ? (
            <span>
              <strong>
                {Prices.PrivatePriceTitle} ${Prices.PrivatePrice}
              </strong>
              <br />
              <em className="product-card__private-price-description">
                {Prices.PrivatePriceDescription}
              </em>
            </span>
          ) : null}
        </div>
        {ProductOffSiteUrl?.Src ? (
          <a
            className="button button--outline product-card__button"
            href={ProductOffSiteUrl?.Src}
            target={ProductOffSiteUrl?.Target}
            type="submit"
          >
            {ProductOffSiteUrl?.Text}
          </a>
        ) : !Prices.PrivatePrice ? (
          <form onSubmit={handleSubmit}>
            <button
              className="button button--outline product-card__button"
              aria-label={`Add ${Title} to cart`}
              type="submit"
            >
              {addToCardLabel}
            </button>
          </form>
        ) : (
          <>
            {IsAuthenticated ? (
              <>
                {IsMember ? (
                  <form onSubmit={handleSubmit}>
                    <button
                      className="button button--outline product-card__button"
                      aria-label={`Add ${Title} to cart`}
                      type="submit"
                    >
                      {addToCardLabel}
                    </button>
                  </form>
                ) : (
                  <div className="mt-6">
                    <strong>{unableToAddToCart}</strong>
                  </div>
                )}
              </>
            ) : (
              <a
                className="button button--outline product-card__button"
                aria-label={`Login to purchase ${Title}`}
                href={LoginUrl}
              >
                {loginToPurchaseLabel}
              </a>
            )}
          </>
        )}
      </div>
    </article>
  );
}

ProductCard.propTypes = {
  unableToAddToCart: PropTypes.string.isRequired,
  pricesLabels: PropTypes.object.isRequired,
  addToCardLabel: PropTypes.string.isRequired,
  cartEndpoint: PropTypes.string.isRequired,
  loginToPurchaseLabel: PropTypes.string.isRequired,
  listNameSufix: PropTypes.string.isRequired,
  Title: PropTypes.string.isRequired,
  Link: PropTypes.string.isRequired,
  Author: PropTypes.string.isRequired,
  Thumb: PropTypes.shape({
    Src: PropTypes.string.isRequired,
    Alt: PropTypes.string.isRequired,
    ImageOverlay: PropTypes.string,
    ImageOverlayLocation: PropTypes.string
  }),
  Prices: PropTypes.shape({
    MemberPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    NonMemberPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    PrivatePriceTitle: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    PrivatePrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    PrivatePriceDescription: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    DpgMemberPriceTitle: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    DpgMemberPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    AvailablePrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ])
  }),
  Description: PropTypes.string.isRequired,
  ProductKey: PropTypes.string.isRequired,
  IsAuthenticated: PropTypes.bool.isRequired,
  IsMember: PropTypes.bool.isRequired,
  LoginUrl: PropTypes.string.isRequired,
  PublishedDateText: PropTypes.string.isRequired,
  SitecoreId: PropTypes.string.isRequired,
  ProductOffSiteUrl: PropTypes.shape({
    Src: PropTypes.string,
    Target: PropTypes.string,
    Text: PropTypes.string
  }),
  ProductImageFlags: PropTypes.array
};

export default ProductCard;
